import React, { useState } from "react";
import { Mail, CheckCircle, XCircle, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import LanguageSwitcher from "../ui/LanguageSwitcher";

const ForgotPasswordView = ({ API_URL }) => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    const navigate = useNavigate();
    const { t } = useTranslation();

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email) {
            showMessage("error", t("validation.enterEmail"));
            return;
        }

        setLoading(true);

        try {
            const res = await fetch(`${API_URL}/auth/forgot-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });

            const data = await res.json();

            if (res.ok) {
                showMessage(
                    "success",
                    data.message || t("messages.resetLinkSent")
                );
            } else {
                showMessage(
                    "error",
                    data.message || t("messages.resetLinkError")
                );
            }
        } catch {
            showMessage("error", t("messages.networkError"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative min-h-screen text-white overflow-hidden">
            <video
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
            >
                <source src="./login-bg.mp4" type="video/mp4" />
            </video>

            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-purple-900/40 to-black/80" />

            <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
                <div className="w-full max-w-md">
                    <div className="bg-white/10 backdrop-blur-xl rounded-2xl my-6 p-8 shadow-[0_0_50px_rgba(255,0,255,0.25)] border border-white/20">
                        <div className="flex justify-end mb-4">
                            <LanguageSwitcher />
                        </div>

                        <div className="text-center mb-6">
                            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/30 backdrop-blur-md">
                                <Mail className="w-10 h-10 text-pink-300" />
                            </div>
                            <h2 className="text-3xl font-bold mb-2">
                                {t("auth.forgot.title")}
                            </h2>
                            <p className="text-gray-200">
                                {t("auth.forgot.subtitle")}
                            </p>
                        </div>

                        {message.text && (
                            <div
                                className={`mb-6 p-4 rounded-xl text-sm flex items-start gap-3 border ${
                                    message.type === "success"
                                        ? "bg-green-500/10 border-green-400 text-green-200"
                                        : "bg-red-500/10 border-red-400 text-red-200"
                                }`}
                            >
                                {message.type === "success" ? (
                                    <CheckCircle className="w-5 h-5" />
                                ) : (
                                    <XCircle className="w-5 h-5" />
                                )}
                                <span>{message.text}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm mb-2">
                                    {t("auth.forgot.emailLabel")}
                                </label>
                                <div className="relative">
                                    <Mail className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 w-5 h-5" />
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder={t("auth.forgot.emailPlaceholder")}
                                        className="w-full pr-10 pl-4 py-3 rounded-xl bg-white/5 border border-white/20 placeholder-gray-300 text-white focus:ring-2 focus:ring-pink-500 outline-none"
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-pink-500 to-red-500 hover:opacity-90 disabled:opacity-50 transition"
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>{t("auth.forgot.sending")}</span>
                                    </div>
                                ) : (
                                    t("auth.forgot.submit")
                                )}
                            </button>
                        </form>

                        <div className="mt-6 text-center">
                            <button
                                onClick={() => navigate("/login")}
                                className="flex items-center justify-center gap-2 text-pink-300 hover:text-pink-100 transition font-medium"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                {t("auth.backToLogin")}
                            </button>
                        </div>
                    </div>

                    <div className="mt-6 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 shadow-[0_0_40px_rgba(255,0,255,0.15)]">
                        <h3 className="font-semibold text-pink-200 mb-3">
                            {t("auth.forgot.helpTitle")}
                        </h3>

                        <ul className="space-y-2 text-sm text-gray-200">
                            <li className="flex items-start gap-2">
                                <span className="text-pink-300 mt-0.5">-</span>
                                <span>{t("auth.forgot.helpSpamCheck")}</span>
                            </li>

                            <li className="flex items-start gap-2">
                                <span className="text-pink-300 mt-0.5">-</span>
                                <span>
                                    <Trans
                                        i18nKey="auth.forgot.helpLinkValidFor"
                                        values={{ minutes: 30 }}
                                        components={[<strong className="text-white" />]}
                                    />
                                </span>
                            </li>

                            <li className="flex items-start gap-2">
                                <span className="text-pink-300 mt-0.5">-</span>
                                <span>{t("auth.forgot.helpContactSupport")}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordView;
